import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Badge,
  Divider,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";

export default function VideoDetailModal({ isOpen, onClose, video, onEdit }) {
  const toast = useToast();

  const handleEdit = () => {
    onClose();
    onEdit(video);
  };

  if (!video) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "6xl" }}>
      <ModalOverlay />
      <ModalContent
        maxW={{ base: "100%", md: "1200px" }}
        maxH={{ base: "100vh", md: "90vh" }}
        overflow="auto"
      >
        <ModalHeader fontSize={{ base: "lg", md: "xl" }}>
          콘텐츠 상세 정보
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex
            gap={{ base: 4, md: 6 }}
            direction={{ base: "column", md: "row" }}
          >
            {/* 썸네일 이미지 */}
            <VStack
              spacing={4}
              w={{ base: "100%", md: "300px" }}
              flexShrink={0}
            >
              <Box
                w={{ base: "100%", md: "300px" }}
                h={{ base: "250px", md: "400px" }}
                bg="gray.100"
                borderRadius="md"
                overflow="hidden"
              >
                {video.thumbnail && (
                  <Image
                    src={video.thumbnail}
                    alt={video.title}
                    w="100%"
                    h="100%"
                    objectFit="contain"
                  />
                )}
              </Box>
              <Button
                colorScheme="blue"
                w="100%"
                onClick={handleEdit}
                size={{ base: "sm", md: "md" }}
              >
                정보 수정하기
              </Button>
            </VStack>

            {/* 상세 정보 */}
            <Box flex={1} minW={0}>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={{ base: 3, md: 4 }}
              >
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Text
                    fontSize={{ base: "xl", md: "2xl" }}
                    fontWeight="bold"
                    mb={2}
                  >
                    {video.title}
                  </Text>
                  <Flex gap={2} mb={4} flexWrap="wrap">
                    <Badge
                      colorScheme={video.open ? "green" : "gray"}
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      {video.open ? "공개" : "비공개"}
                    </Badge>
                    <Badge
                      colorScheme="blue"
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      {video.video_type}
                    </Badge>
                    <Badge fontSize={{ base: "xs", md: "sm" }}>
                      {video.nation}
                    </Badge>
                    <Badge fontSize={{ base: "xs", md: "sm" }}>
                      {video.published_year}
                    </Badge>
                  </Flex>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Text
                    color="gray.600"
                    mb={4}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    {video.introduction}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    감독
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.director || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    출연
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.cast || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    장르
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.genre || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    공개 플랫폼
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.ott || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    권리자
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.copyrights_holder_name || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    식별 코드
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.identification_code || "-"}
                  </Text>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Box
                    borderWidth={1}
                    borderRadius="md"
                    p={{ base: 3, md: 4 }}
                    mb={4}
                  >
                    <Text
                      fontWeight="bold"
                      mb={2}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      숏츠 제작 가이드
                    </Text>
                    <Text
                      whiteSpace="pre-wrap"
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      {video.guide || "-"}
                    </Text>
                  </Box>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Box
                    borderWidth={1}
                    borderRadius="md"
                    p={{ base: 3, md: 4 }}
                    mb={4}
                  >
                    <Text
                      fontWeight="bold"
                      mb={2}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      숏츠 제작시 별도 안내 사항
                    </Text>
                    <Text
                      whiteSpace="pre-wrap"
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      {video.comments || "-"}
                    </Text>
                  </Box>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Divider my={4} />
                  <Text
                    fontWeight="bold"
                    mb={2}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    추가 정보
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    Whitelist
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.whitelist || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    Scope
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.scope || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    채널 타입
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.channel_type === "A" ? "브랜드 채널" : "일반 채널"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    롱폼 여부
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.is_longform ? "예" : "아니오"}
                  </Text>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    Notes
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {video.notes || "-"}
                  </Text>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Divider my={4} />
                  <Text
                    fontWeight="bold"
                    mb={2}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    수익 배분율
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    size="sm"
                    mb={2}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    음원
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    Laeebly: {video.share_music_laeebly || 0}%
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    크리에이터: {video.share_music_creator || 0}%
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    저작권자: {video.share_music_copyright || 0}%
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    size="sm"
                    mb={2}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    영상
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    Laeebly: {video.share_video_laeebly || 0}%
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    크리에이터: {video.share_video_creator || 0}%
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    저작권자: {video.share_video_copyright || 0}%
                  </Text>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Text
                    fontWeight="bold"
                    size="sm"
                    mb={2}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    쇼핑
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    Laeebly: {video.share_shopping_laeebly || 0}%
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    크리에이터: {video.share_shopping_creator || 0}%
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    저작권자: {video.share_shopping_copyright || 0}%
                  </Text>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Divider my={4} />
                  <Text
                    fontWeight="bold"
                    mb={2}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    링크
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    플랫폼 영상 링크
                  </Text>
                  <Text
                    color="blue.500"
                    cursor="pointer"
                    onClick={() => window.open(video.url_submit_link, "_blank")}
                    fontSize={{ base: "sm", md: "md" }}
                    noOfLines={1}
                  >
                    {video.url_submit_link || "-"}
                  </Text>
                </GridItem>

                <GridItem>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    예고편 링크
                  </Text>
                  <Text
                    color="blue.500"
                    cursor="pointer"
                    onClick={() => window.open(video.trailer_url, "_blank")}
                    fontSize={{ base: "sm", md: "md" }}
                    noOfLines={1}
                  >
                    {video.trailer_url || "-"}
                  </Text>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Text
                    fontWeight="bold"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    다운로드 링크
                  </Text>
                  <Text
                    color="blue.500"
                    cursor="pointer"
                    onClick={() => window.open(video.download_link, "_blank")}
                    fontSize={{ base: "sm", md: "md" }}
                    noOfLines={1}
                  >
                    {video.download_link || "-"}
                  </Text>
                </GridItem>
              </Grid>
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
